/**
 * Hub (親メニュー) の汎用タブシェル。
 *
 * v3.3 IA 刷新で導入。各 Hub ページが TabSpec[] を渡し、URL `?tab=` で
 * タブ切り替えを行う。タブごとに featureId × view 権限でフィルタする。
 *
 * 設計方針:
 * - タブ未指定時は最初の可視タブにリダイレクト
 * - 全タブが不可視 (= permission denied) なら AccessGate に委譲してエラー表示
 * - タブ切替時は URL を書き換え (history.replaceState) してリロード/共有可能に
 * - タブ内 content は遅延描画しない (Radix Tabs はマウントを保持する設計)
 */
import { useEffect, useMemo, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useMyPermissions } from '@/features/permissions';
import { hasPermission } from '@goenhub/shared';

export interface TabSpec {
  /** タブ識別子 (URL の `?tab=` 値)。features.ts の FeatureItem.tabKey と一致させる。 */
  tabKey: string;
  /** 表示ラベル。 */
  label: string;
  /** 権限判定に使う featureId。 */
  featureId: string;
  /** タブ本体。React node を即時 evaluate するか、function を渡す。 */
  render: () => ReactNode;
  /** タブ左に表示する icon (任意)。 */
  icon?: ReactNode;
  /** バッジ表示する数値 (任意)。 */
  count?: number;
}

interface HubTabsProps {
  tabs: TabSpec[];
  /** 既定タブ。指定なしの場合は可視タブの先頭。 */
  defaultTab?: string;
  /**
   * URL クエリパラメータ名。既定: 'hub'。
   * 'tab' は埋め込まれた内部ページ (UsersPage / ReferralPage 等) が
   * 自前の内部タブ管理に使うため、Hub 側は 'hub' を使う。
   */
  queryKey?: string;
}

export function HubTabs({ tabs, defaultTab, queryKey = 'hub' }: HubTabsProps) {
  const { isOwner, permissions } = useMyPermissions();
  const [searchParams, setSearchParams] = useSearchParams();

  const visibleTabs = useMemo(
    () =>
      tabs.filter((t) =>
        hasPermission({ isOwner, permissions, featureId: t.featureId, action: 'view' }),
      ),
    [tabs, isOwner, permissions],
  );

  const currentTab = searchParams.get(queryKey);
  const activeTab =
    visibleTabs.find((t) => t.tabKey === currentTab)?.tabKey ??
    visibleTabs.find((t) => t.tabKey === defaultTab)?.tabKey ??
    visibleTabs[0]?.tabKey ??
    null;

  // URL に tab が指定されていない / 不正な場合は、可視の既定タブに replace
  useEffect(() => {
    if (!activeTab) return;
    if (currentTab !== activeTab) {
      const next = new URLSearchParams(searchParams);
      next.set(queryKey, activeTab);
      setSearchParams(next, { replace: true });
    }
  }, [activeTab, currentTab, queryKey, searchParams, setSearchParams]);

  if (visibleTabs.length === 0) {
    return (
      <div className="text-sm text-[var(--text-muted)] p-8 text-center">
        このセクションへのアクセス権限がありません。
      </div>
    );
  }

  if (!activeTab) return null;

  return (
    <Tabs
      value={activeTab}
      onValueChange={(value) => {
        const next = new URLSearchParams(searchParams);
        next.set(queryKey, value);
        setSearchParams(next, { replace: false });
      }}
      className="hub-tabs"
    >
      <TabsList className="hub-tabs-list">
        {visibleTabs.map((t) => (
          <TabsTrigger key={t.tabKey} value={t.tabKey}>
            <span className="inline-flex items-center gap-1.5">
              {t.icon}
              {t.label}
              {typeof t.count === 'number' && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--bg-hover)] text-[var(--text-secondary)]">
                  {t.count}
                </span>
              )}
            </span>
          </TabsTrigger>
        ))}
      </TabsList>
      {visibleTabs.map((t) => (
        <TabsContent key={t.tabKey} value={t.tabKey} className="hub-tab-content">
          {t.render()}
        </TabsContent>
      ))}
    </Tabs>
  );
}
